import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, type BudgetAllocation } from '../db/database'
import { confirmAllocation } from '../db/budget'
import { cycleRange } from '../lib/cycles'
import { formatAmount, parseAmountBounded } from '../lib/money'
import { formatDay, today } from '../lib/dates'
import { useCycleLocked } from '../lib/lock'
import './PendingConfirmations.css'

/**
 * Ce qui attend une confirmation.
 *
 * Le même bloc sur l'accueil et dans les opérations, parce que c'est le même
 * geste : dire ce qui a réellement été dépensé. L'accueil ne montre que le jour
 * en cours — c'est la boucle quotidienne. Les opérations montrent TOUT le cycle,
 * y compris les journées passées qu'on a oublié de confirmer : c'est là qu'on
 * vient rattraper son retard, et un oubli invisible finit en solde faux.
 */
/**
 * Ce qu'on montre.
 *
 *   today          la journée en cours — la boucle quotidienne de l'accueil
 *   cycle          tout le cycle, retard compris — l'écran des opérations
 *   { day }        une seule date, celle d'une notification
 *
 * Le troisième est arrivé avec les notifications (2026-09-05) : une
 * notification qui dépose sur une liste laisse l'utilisateur chercher ce qu'on
 * vient de lui annoncer.
 */
export type PendingScope = 'today' | 'cycle' | { day: string }

export function PendingConfirmations(
  { walletId, scope, bare = false }: {
    walletId: string
    scope: PendingScope
    /** Sans titre ni cadre : la feuille d'une journée porte déjà les siens. */
    bare?: boolean
  },
) {
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [confirming, setConfirming] = useState<Set<string>>(new Set())
  const locked = useCycleLocked()

  const data = useLiveQuery(async () => {
    const wallet = await db.wallets.get(walletId)
    const range = cycleRange(today(), wallet?.cycle ?? 'monthly')
    const rows = (await db.budget_allocations.where({ wallet_id: walletId }).toArray())
      .filter((a) => !a.deleted_at && !a.confirmed_at)
      // JAMAIS l'avenir, même dans les opérations : proposer de tout confirmer
      // d'avance viderait l'app de son intérêt, qui est d'être ouverte chaque
      // jour. On ne montre que ce qui est dû — aujourd'hui, et le retard.
      // JAMAIS l'avenir non plus pour une date précise : une notification ne
      // peut désigner qu'un jour déjà venu.
      .filter((a) => typeof scope === 'object'
        ? a.planned_date === scope.day && a.planned_date <= today()
        : scope === 'today'
          ? a.planned_date === today()
          : a.planned_date >= range.start && a.planned_date <= today())
      .sort((a, b) => a.planned_date.localeCompare(b.planned_date))
    const categories = await db.categories.where({ wallet_id: walletId }).toArray()
    return { rows, categories }
    // `scope` sérialisé : un objet littéral est une NOUVELLE référence à
    // chaque rendu, et `useLiveQuery` relancerait sa lecture indéfiniment.
  }, [walletId, typeof scope === 'object' ? `day:${scope.day}` : scope], undefined)

  if (!data || data.rows.length === 0) return null

  const nameOf = (id: string) =>
    data.categories.find((category) => category.id === id)?.name ?? 'Enveloppe'

  async function confirm(allocation: BudgetAllocation) {
    // Un double-tap relit la même allocation avant que le premier appel n'ait
    // posé `confirmed_at` : sans ce garde, les deux écrivent avant que la
    // ligne ne disparaisse de la liste.
    if (confirming.has(allocation.id)) return
    const draft = drafts[allocation.id]
    const amount = draft === undefined ? allocation.planned_amount : parseAmountBounded(draft)
    if (amount === null) return
    setConfirming((c) => new Set(c).add(allocation.id))
    try {
      // Jamais attendu au-delà de l'écriture locale : confirmAllocation écrit
      // dans Dexie et rend la main, la synchro part seule.
      await confirmAllocation(allocation, amount)
    } finally {
      setConfirming((c) => {
        const next = new Set(c)
        next.delete(allocation.id)
        return next
      })
    }
  }

  return (
    <section className={`pending ${bare ? 'is-bare' : ''}`}>
      {!bare && (
        <header className="section-head">
          <h2>{scope === 'today' ? "Prévu aujourd'hui" : 'À confirmer'}</h2>
          {scope === 'cycle' && data.rows.some((a) => a.planned_date < today()) && (
            <span className="pending-late">journées passées non confirmées</span>
          )}
        </header>
      )}

      {/* `fieldset disabled` et non un `disabled` par contrôle : il désactive
          nativement TOUS les champs et boutons descendants, y compris ceux qu'on
          ajoutera plus tard. Un verrou qu'il faut penser à recopier finit par
          être oublié — et un oubli ici laisse écrire pendant une clôture. */}
      <fieldset className="bare" disabled={locked}>
        <ul className="rows">
          {data.rows.map((allocation) => {
            const enRetard = allocation.planned_date < today()
            return (
              <li key={allocation.id} className={`pending-row ${enRetard ? 'is-late' : ''}`}>
                <span className="pending-what">
                  <strong>{nameOf(allocation.category_id)}</strong>
                  <span className="pending-meta">
                    {/* La date seulement quand la liste en mêle plusieurs : une
                        feuille de journée la porte déjà dans son titre. */}
                    {scope === 'cycle' && formatDay(allocation.planned_date)}
                    {scope === 'cycle' && allocation.label && ' · '}
                    {allocation.label}
                  </span>
                </span>
                <span className="pending-field">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="tabular"
                    aria-label={`Montant dépensé pour ${nameOf(allocation.category_id)}`}
                    value={drafts[allocation.id] ?? formatAmount(allocation.planned_amount)}
                    onChange={(event) =>
                      setDrafts((c) => ({ ...c, [allocation.id]: event.target.value }))}
                  />
                  <span aria-hidden="true">Ar</span>
                </span>
                <button type="button" className="pending-confirm"
                        disabled={confirming.has(allocation.id)}
                        onClick={() => void confirm(allocation)}>
                  Confirmer
                </button>
              </li>
            )
          })}
        </ul>
      </fieldset>
    </section>
  )
}
